import React from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView, useAnimation } from 'framer-motion';

// import './WorkProcess.css';

const WorkProcess = () => {
  const [animationComplete, setAnimationComplete] = React.useState({});
  const processes = [
    {
      number: '1',
      title: 'Tell us what you need',
      description: 'Share your hiring needs to help us screen the top 2% candidates from our talent network.',
    },
    {
      number: '2',
      title: 'Interview Our Talent',
      description: "We'll connect you with hand picked, pre screened candidates perfectly aligned with your requirements.",
    },
    {
      number: '3',
      title: 'Onboard Your Hire',
      description: "Welcome your new remote hire to the team, while we've got everything else covered.",
    },
  ];

  const ProcessStep = ({ process, index, isLast }) => {
    const ref = React.useRef(null);
    const isInView = useInView(ref, { once: true, margin: "-100px" });
    const numberControls = useAnimation();
    const lineControls = useAnimation();
    const textControls = useAnimation();
    const progressControls = useAnimation();
    const [showNumber, setShowNumber] = React.useState(false);

    React.useEffect(() => {
      const sequence = async () => {
        if (isInView) {
          // Reset all animations
          setShowNumber(false);
          await Promise.all([
            numberControls.start({
              scale: 0,
              opacity: 0,
              transition: { duration: 0 }
            }),
            lineControls.start({
              scaleX: 0,
              opacity: 0,
              transition: { duration: 0 }
            }),
            textControls.start({
              opacity: 0,
              y: 20,
              transition: { duration: 0 }
            }),
            progressControls.start({
              width: "0%",
              transition: { duration: 0 }
            })
          ]);

          // Calculate delays for each step
          const baseDelay = index * 1.5; // Reduced total delay between steps for faster animation

          // Number animation with bounce effect
          await numberControls.start({
            scale: [0, 1.2, 1],
            opacity: 1,
            transition: { 
              duration: 0.5,
              delay: baseDelay,
              ease: "easeOut",
              times: [0, 0.8, 1]
            }
          });

          // Wait before starting text
          await new Promise(resolve => setTimeout(resolve, 250));

          // Text animation
          await textControls.start({
            opacity: 1,
            y: 0,
            transition: { 
              duration: 0.4,
              ease: "easeOut"
            }
          });

          // Wait before starting line
          await new Promise(resolve => setTimeout(resolve, 150));

          // Line animation
          if (!isLast) {
            await lineControls.start({
              scaleX: 1,
              opacity: 1,
              transition: { 
                duration: 0.6,
                ease: "easeInOut"
              }
            });
          }

        } else {
          // Reset when out of view
          await Promise.all([
            numberControls.start({
              scale: 0,
              opacity: 0,
              transition: { duration: 0.3, ease: "easeIn" }
            }),
            lineControls.start({
              scaleX: 0,
              opacity: 0,
              transition: { duration: 0.3, ease: "easeIn" }
            }),
            textControls.start({
              opacity: 0,
              y: 20,
              transition: { duration: 0.3, ease: "easeIn" }
            })
          ]);
        }
      };
      sequence();
    }, [isInView, numberControls, lineControls, textControls, index, isLast]);

    return (
      <div className="process-step-container" ref={ref}>
        <div className="process-content">
          <div className="process-number-container">
            <motion.div
              className="process-number"
              initial={{ opacity: 0, scale: 0.5 }}
              animate={numberControls}
            >
              {process.number}
            </motion.div>
            {!isLast && (
              <motion.div
                className="process-line"
                initial={{ scaleX: 0, opacity: 0 }}
                animate={lineControls}
                style={{ originX: 0 }}
              />
            )}
          </div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={textControls}
          >
            <motion.h3 
              className="process-title"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ 
                duration: 0.4,
                delay: index * 1.5 + 0.75
              }}
            >
              {process.title}
            </motion.h3>
            <motion.p 
              className="process-description"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ 
                duration: 0.4,
                delay: index * 1.5 + 1.0 }}
            >
              {process.description}
            </motion.p>
          </motion.div>
        </div>
      </div>
    );
  };

  return (
    <section className="work-process-section">
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-lg-8">
            <div className="section-heading text-center">
              <h2 className="heading-text">
                Hire Talent
              </h2>
              <p className="heading-description">
                From screening to onboarding, we help you hire remote talent without the usual delays.
              </p>
            </div>
          </div>
        </div>
        <div className="process-steps-container">
          {processes.map((process, index) => (
            <ProcessStep 
              key={index} 
              process={process} 
              index={index}
              isLast={index === processes.length - 1}
            />
          ))}
        </div>

        <div className="action-buttons text-center">
          <Link to="/contact" className="btn btn-primary hire-now-btn">Hire Now</Link>
          <Link to="/Pricing" className="btn btn-outline-primary explore-btn">Explore Pricing</Link>
        </div>
      </div>
    </section>
  );
};
export default WorkProcess;